package com.example.mystybag.url.views

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.ensureGoogleSansFontFacesForDom
import com.example.mystybag.core.expected.googleSansFontFamilyCss
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.url.logic.URLViewViewModel
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLScriptElement
import org.w3c.dom.events.Event

private const val PAYMENT_SUCCESS_FOOTER_ID = "url-view-reservations-footer"

@Composable
actual fun URLView(viewModel: URLViewViewModel) {
    val state = viewModel.uiState.subscribeAsState()
    val url = state.value.url
    val postBody = state.value.postBody
    val showPaymentSuccessFooter = state.value.showPaymentSuccessFooter

    DisposableEffect(showPaymentSuccessFooter) {
        if (showPaymentSuccessFooter) {
            if (document.getElementById(PAYMENT_SUCCESS_FOOTER_ID) == null) {
                val bar = (document.createElement("div") as HTMLDivElement).apply {
                    id = PAYMENT_SUCCESS_FOOTER_ID
                    style.position = "fixed"
                    style.left = "0"
                    style.right = "0"
                    style.bottom = "0"
                    style.zIndex = "10000"
                    style.display = "flex"
                    style.justifyContent = "center"
                    style.alignItems = "center"
                    // Match PaymentSuccessReservationsFooter / DefaultButton LARGE: p16 horizontal, p8 vertical + safe area
                    style.paddingLeft = "16px"
                    style.paddingRight = "16px"
                    style.paddingTop = "8px"
                    style.paddingBottom = "calc(8px + env(safe-area-inset-bottom, 0px))"
                    style.background = "#FFFFFF"
                }
                val btn = (document.createElement("button") as HTMLButtonElement).apply {
                    ensureGoogleSansFontFacesForDom()
                    textContent = sharedString(SR.strings.my_reservations)
                    style.width = "100%"
                    style.maxWidth = "480px"
                    style.boxSizing = "border-box"
                    style.height = "56px"
                    style.borderRadius = "100px"
                    style.border = "none"
                    // DefaultButtonType.PRIMARY → light scheme sr_primary / sr_on_primary (LocalTheme / colors.xml)
                    style.background = "#2AA814"
                    style.color = "#FFFFFF"
                    style.fontSize = "16px"
                    style.fontWeight = "600"
                    // Same family as Reserve / DefaultButton (LocalTheme.googleSans)
                    style.fontFamily = googleSansFontFamilyCss()
                    style.cursor = "pointer"
                    style.boxShadow = "0px 1px 2px rgba(0,0,0,0.08), 0px 1px 3px rgba(0,0,0,0.06)"
                }
                btn.addEventListener("click", { viewModel.onReservationsCtaClick() })
                bar.appendChild(btn)
                document.body?.appendChild(bar)
            }
        } else {
            document.getElementById(PAYMENT_SUCCESS_FOOTER_ID)?.remove()
        }
        onDispose {
            document.getElementById(PAYMENT_SUCCESS_FOOTER_ID)?.remove()
        }
    }

    DisposableEffect(url, postBody) {
        val host = (document.createElement("div") as HTMLDivElement).apply {
            id = "embeddedCheckout"
            style.position = "fixed"
            style.top = "0"
            style.left = "0"
            style.width = "100vw"
            style.height = "100vh"
            style.background = "#FFFFFF"
            style.zIndex = "9999"
        }
        document.body?.appendChild(host)

        var iframe: HTMLIFrameElement? = null
        var form: HTMLFormElement? = null

        fun renderLegacyIframeCheckout(vm: URLViewViewModel) {
            iframe = (document.createElement("iframe") as HTMLIFrameElement).apply {
                if (postBody.isBlank()) {
                    src = url
                }
                style.width = "100%"
                style.height = "100%"
                style.border = "0"
                style.background = "#FFFFFF"
                name = "myposCheckoutFrame"
                setAttribute("allow", "payment *")
            }
            iframe!!.addEventListener("load", iframeLoad@{ _: Event ->
                val frame = iframe ?: return@iframeLoad
                val href = readIframeLocationHrefOrNull(frame) ?: return@iframeLoad
                if (href.isNotBlank() && href != "about:blank") {
                    vm.onNavigatedUrl(href)
                }
            })
            host.appendChild(iframe!!)

            if (postBody.isNotBlank()) {
                form = (document.createElement("form") as HTMLFormElement).apply {
                    method = "POST"
                    action = url
                    target = iframe!!.name
                    style.display = "none"
                }
                postBody.split("&").forEach { part ->
                    val token = part.split("=", limit = 2)
                    val key = decodeURIComponentSafe(token.firstOrNull().orEmpty())
                    val value = decodeURIComponentSafe(token.getOrNull(1).orEmpty())
                    val input = document.createElement("input") as HTMLInputElement
                    input.type = "hidden"
                    input.name = key
                    input.value = value
                    form?.appendChild(input)
                }
                document.body?.appendChild(form)
                form?.submit()
            }
        }

        if (postBody.isBlank()) {
            renderLegacyIframeCheckout(viewModel)
        } else {
            renderEmbeddedSdkCheckout(
                containerId = host.id,
                postBody = postBody,
                onFallback = { renderLegacyIframeCheckout(viewModel) },
                onPaymentCompleted = { viewModel.onCheckoutSuccessSignal() }
            )
        }

        onDispose {
            form?.remove()
            iframe?.remove()
            host.remove()
        }
    }
}

private fun readIframeLocationHrefOrNull(iframe: HTMLIFrameElement): String? =
    try {
        val loc = iframe.contentWindow?.asDynamic()?.location ?: return null
        (loc.href as? String)?.takeIf { it.isNotBlank() }
    } catch (_: Throwable) {
        null
    }

private fun renderEmbeddedSdkCheckout(
    containerId: String,
    postBody: String,
    onFallback: () -> Unit,
    onPaymentCompleted: () -> Unit
) {
    val render = {
        try {
            val sdk = js("window.MyPOSEmbedded")
            if (js("!sdk || typeof sdk.createPayment !== 'function'") as Boolean) {
                onFallback()
            } else {
                val paymentParams = parsePostBodyToDynamic(postBody)
                val callbackParams = js("{}")
                callbackParams.onSuccess = { _: dynamic -> onPaymentCompleted() }
                val customization = js("{}")
                customization.formBackgroundColor = "#FFFFFF"
                customization.labelFontSize = "13px"
                customization.labelColor = "#2D2E2F"
                customization.inputBorderRadius = "12px"
                customization.payButtonBorderColor = "#2fc40a"
                customization.payButtonBackgroundColor = "#2fc40a"
                customization.payButtonBorderColorHover = "#27a807"
                customization.payButtonBackgroundColorHover = "#27a807"
                customization.payButtonColor = "#FFFFFF"
                customization.payButtonColorHover = "#FFFFFF"
                customization.payButtonFontSize = "16px"
                sdk.createPayment(containerId, paymentParams, callbackParams, customization)
            }
        } catch (_: dynamic) {
            onFallback()
        }
    }

    val w = window.asDynamic()
    if (js("w.MyPOSEmbedded && typeof w.MyPOSEmbedded.createPayment === 'function'") as Boolean) {
        render()
        return
    }

    val script = document.createElement("script") as HTMLScriptElement
    script.src = "https://cdn.jsdelivr.net/npm/mypos-embedded-checkout@latest/dist/mypos-embedded-checkout.min.js"
    script.async = true
    script.asDynamic().onload = { _: dynamic -> render() }
    script.asDynamic().onerror = { _: dynamic, _: dynamic, _: dynamic, _: dynamic, _: dynamic -> onFallback() }
    document.head?.appendChild(script)
}

private fun parsePostBodyToDynamic(postBody: String): dynamic {
    val obj = js("{}")
    postBody.split("&").forEach { part ->
        val token = part.split("=", limit = 2)
        val key = decodeURIComponentSafe(token.firstOrNull().orEmpty())
        val value = decodeURIComponentSafe(token.getOrNull(1).orEmpty())
        if (key.isNotBlank()) obj[key] = value
    }
    return obj
}

private fun decodeURIComponentSafe(value: String): String {
    val normalized = value.replace("+", "%20")
    return try {
        js("decodeURIComponent")(normalized) as String
    } catch (_: dynamic) {
        value
    }
}
